import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Grid, Header, Segment, Card, Icon, Statistic, Image, Button, Divider } from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import { getMenuIdForFeature } from '../../utils/roleUtils';
import { fetchStatistics, selectStatisticsByView, selectStatisticsLoading } from '../../slices/statisticsSlice';
import { STAT_SETS } from '../../constants/statisticsFlags';
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import bookStack from '../../assets/justBookStack.svg';
import checkBox from '../../assets/checkListLine.svg';
import voiceActressBW from '../../assets/micAndshield.svg';
import lightening from '../../assets/lightening.svg';
import myLibrary from '../../assets/myLibrary.svg'
import bookDetails from '../../assets/bookDetails.svg'
import userPuzzle from '../../assets/userPuzzle.svg'
import recordingPlayer from '../../assets/recordingPlayer.svg'
import robotGearRocket from '../../assets/robotGearRocket.svg'
import profileIcon from '../../assets/profileLineGlasses.svg'
import microphoneStand from '../../assets/microphone-stand.svg'
import voiceActor from '../../assets/shutterstock_2294366549-480x480.png'

const AdminLandingPage = () => {
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.profile);
  const userName = userProfile?.data?.title || 'Administrator';

  // Fetch statistics from Redux
  const stats = useSelector(selectStatisticsByView('admin_dashboard'));
  const isLoadingStats = useSelector(selectStatisticsLoading('admin_dashboard'));

  // Fetch statistics on component mount
  useEffect(() => {
    dispatch(fetchStatistics({
      viewType: 'admin_dashboard',
      flags: STAT_SETS.ADMIN_DASHBOARD
    }));
  }, [dispatch]);

  // Quick action cards for admin
  const quickActions = [
    {
      icon: 'users',
      img: userPuzzle, // Using bookStack as a placeholder for users icon
      title: 'Manage Users',
      description: 'Add, edit, or remove user accounts',
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('userManagement') }
    },
    {
      icon: 'microphone',
      img: voiceActressBW,
      title: 'Voice Lab',
      description: 'Manage voice actors and recordings',
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('voiceLab') }
    },
    {
      icon: 'book',
      img: bookDetails,
      title: 'Book Worksheet',
      description: 'View and manage all books in the system',
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      icon: 'file text',
      img: myLibrary, // Using hourGlass as a placeholder for text library icon
      title: 'Text Library',
      description: 'Access and manage the text library',
      link: '/add-books-landing',
      state: { menuSelection: getMenuIdForFeature('textLibrary') }
    },
    {
      icon: 'pencil',
      img: bookStack, // Using bookStack as a placeholder for Book Creator Worksheet
      title: 'Book Creator',
      description: 'Create and manage literary groups and books',
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookCreatorWorksheet') }
    },
    {
      icon: 'pencil',
      img: voiceActor, // Using bookStack as a placeholder for Book Creator Worksheet
      title: 'Voice Actor',
      description: 'Create and manage voice actor worksheet',
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('voiceActorWorksheetEnh') }
    },
    {
      icon: 'sitemap',
      img: microphoneStand,
      title: 'Opportunities',
      description: 'Manage Marketplace',
      link: '/opportunities',
      state: {}
    }, {
      icon: 'sitemap',
      img: robotGearRocket,
      title: 'Path Registry',
      description: 'Manage S3 path definitions',
      link: '/admin/path-registry',
      state: {}
    }, {
      icon: 'shield',
      img: voiceActressBW,
      title: 'Security Registry',
      description: 'Manage security definitions and access control',
      link: '/admin/security-registry',
      state: {}
    }, {
      icon: 'exchange',
      img: robotGearRocket,
      title: 'Emotion → Category Matrix',
      description: 'Edit the emotion-to-category translation used by the renderer',
      link: '/admin/emotion-category-matrix',
      state: {}
    }, {
      icon: 'download',
      img: recordingPlayer,
      title: 'Client Downloads',
      description: 'Release finished audio to clients and issue their access keys',
      link: '/admin/client-downloads',
      state: {}
    }, {
      icon: 'clipboard list',
      img: recordingPlayer,
      title: 'Reader Notes',
      description: 'What the quality-control readers found, by book',
      link: '/admin/reader-notes',
      state: {}
    }, {
      icon: 'mail outline',
      img: recordingPlayer,
      title: 'Reader Access',
      description: 'Tick what a reader may hear, and invite them by email',
      link: '/admin/reader-access',
      state: {}
    }
    // {
    //   icon: 'code',
    //   img: robotGearRocket,
    //   title: 'Path Registry Example',
    //   description: 'Test the new path registry utilities',
    //   link: '/admin/path-registry-example',
    //   state: {}
    // }
  ];

  // System statistics for admin dashboard - now using real data from Redux
  const statistics = [
    {
      label: 'Users',
      value: isLoadingStats ? '...' : (stats?.userCount || '0'),
      icon: 'users',
      img: userPuzzle,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('userManagement') }
    },
    {
      label: 'Books',
      value: isLoadingStats ? '...' : (stats?.totalBooksCount || '0'),
      icon: 'book',
      img: bookStack,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      label: 'Voice Actors',
      value: isLoadingStats ? '...' : (stats?.voiceActorCount || '0'),
      icon: 'microphone',
      img: voiceActressBW,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('voiceLab') }
    },
    {
      label: 'Recordings',
      value: isLoadingStats ? '...' : (stats?.recordingCount || '0'),
      icon: 'file audio',
      img: recordingPlayer,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('voiceLabMyRecordings') }
    },
    {
      label: 'My Profile',
      value: '',
      icon: 'user',
      img: profileIcon,
      link: '/profile'
    }
  ];

  return (
    <Container fluid className="landing-page admin-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src="/static/media/thodkin_librarian_futuristic_2a188bb5-2c71-4330-a561-ef70e9707b82.69a27af8cce99fd638a3.png" />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Welcome, {userName}</span>
              <span className="blgListSubTitle">Administrator Dashboard</span>
              <p className="landing-subheading-text">
                As an administrator, you have full access to manage users, books, voice actors, and system settings.
                Use the quick actions below to navigate to common tasks.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  size="large"
                  className="landing-goto-button"
                >
                  Go to Main Dashboard
                </Button>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container>
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <Header as="h2">
                <img src={robotGearRocket} alt="" style={{ width: '90px' }} />
                <Header.Content style={{
                  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                  fontWeight: '400',
                  fontSize: '2.5rem'
                }}>
                  System Overview
                  <Header.Subheader>Key metrics and statistics</Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={10}>
              <div>
                <Statistic.Group widths="five">
                  {statistics.map((stat, index) => {
                    const StatisticContent = (
                      <>
                        <Statistic.Value>
                          <div style={{
                            fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                            fontWeight: '300',
                            fontSize: '3.0rem'
                          }}><img src={stat.img} alt="" /> {stat.value}</div>
                        </Statistic.Value>
                        <Statistic.Label style={{
                          fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                          fontWeight: '300',
                          fontSize: '1.1rem'
                        }}>{stat.label}</Statistic.Label>
                      </>
                    );

                    // If link is provided, wrap with Link component
                    if (stat.link) {
                      return (
                        <Statistic
                          key={index}
                          as={Link}
                          to={stat.link}
                          state={stat.state}
                          className="statistic-link"
                        >
                          {StatisticContent}
                        </Statistic>
                      );
                    }

                    // Otherwise, render as a regular Statistic
                    return (
                      <Statistic key={index}>
                        {StatisticContent}
                      </Statistic>
                    );
                  })}
                </Statistic.Group>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider />

        <Header as="h2" dividing style={{ marginTop: '2em' }}>
          <img src={lightening} alt="" />
          <Header.Content>
            Quick Actions
            <Header.Subheader>Common administrative tasks</Header.Subheader>
          </Header.Content>
        </Header>

        <Card.Group itemsPerRow={4} stackable>
          {quickActions.map((action, index) => (
            <Card
              key={index}
              as={Link}
              to={action.link}
              state={action.state}
              raised
            >
              <Card.Content>
                <img src={action.img} alt="" style={{ height: '80px', display: 'block', margin: '0 auto 1em', color: '#3d6a87' }} />
                <Card.Header textAlign="center">{action.title}</Card.Header>
                <Card.Description textAlign="center">
                  {action.description}
                </Card.Description>
              </Card.Content>
            </Card>
          ))}
        </Card.Group>

        <Grid columns={2} stackable style={{ marginTop: '2em' }}>
          <Grid.Column>
            <Header as="h2" dividing>
              <img src={bookStack} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Recent Activity
                <Header.Subheader>Latest system events</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <Grid columns={2} stackable>
                <Grid.Column>
                  <Header as="h3">New Users</Header>
                  <p>3 new users registered in the last 7 days</p>
                  <Button compact>View All Users</Button>
                </Grid.Column>
                <Grid.Column>
                  <Header as="h3">System Updates</Header>
                  <p>Voice processing system updated to version 2.4</p>
                  <Button compact>View System Logs</Button>
                </Grid.Column>
              </Grid>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Header as="h2" dividing>
              <img src={checkBox} alt="" style={{ height: '50px' }} />
              <Header.Content>
                System Health
                <Header.Subheader>Current system status</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <p>All services are running normally</p>
              <p>Last backup: Today at 2:00 AM</p>
              <p>Storage usage: 68% (342GB / 500GB)</p>
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  state={{ menuSelection: 'SYSTEM-SETTINGS' }}
                  compact
                >
                  System Settings
                </Button>
              </div>
            </Segment>

            <Segment>
              <Header as="h4">Maintenance</Header>
              <p>Next scheduled maintenance: April 20, 2025</p>
              <Button
                as={Link}
                to="/dashboard"
                state={{ menuSelection: 'SYSTEM-MAINTENANCE' }}
                fluid
                style={{ backgroundColor: '#3d6a87', color: 'white' }}
              >
                <Icon name="wrench" /> Schedule Maintenance
              </Button>
            </Segment>
          </Grid.Column>
        </Grid>
      </Container>

      {AuthStdFooter()}
    </Container>
  );
};

export default AdminLandingPage;
